import { format } from 'date-fns'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookType } from 'xlsx'

import { Add, Key, Person, PersonOff, Restore } from '@mui/icons-material'
import { Box, Button, Dialog, DialogContent, Stack, Tooltip, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { ExportDataButton } from '../../../../../components/ExportDataButton/ExportDataButton'
import GRDialogTitle from '../../../../../components/GRDialogTitle/GRDialogTitle'
import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { isFetchBaseQueryError } from '../../../../../helpers/isFetchBaseQueryError'
import utilsService from '../../../../../services/UtilsService'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import {
  useGetInternalOrganizationUsersQuery,
  useRemoveInternalOrganizationUserMutation,
  useResetInternalUserPasswordMutation,
  useSetInternalOrganizationUserActiveMutation,
} from '../../../../api/organizations'
import { EditUserDialog } from '../../../../components/EditUserDialog/EditUserDialog'
import { generatePassword } from '../../../../components/EditUserDialog/editUserHelpers'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { RowActions } from '../../../../components/RowActions/RowActions'
import { UserStatusIcon, userStatusOrder } from '../../../../components/UserStatusIcon/UserStatusIcon'
import { getValidationErrorMessage } from '../../../../helpers/apiValidationError'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { InternalOrganization, InternalUser } from '../../types/Organizations'
import { exportOrganizationUsers, getUserDisplayName, getUserStatus } from '../helpers/organizationUserHelpers'

type OrganizationUsersDialogProps = {
  open: boolean
  organization?: InternalOrganization
  onClose: () => void
}

type CustomProps = {
  onEdit: (user: InternalUser) => void
  onToggleActive: (user: InternalUser) => void
  onResetPassword: (user: InternalUser) => void
  onRemove: (user: InternalUser) => void
}

type PendingAction = { kind: 'toggle' | 'reset' | 'remove'; user: InternalUser }

const nameOrDash = (value: string | null) => value || '---'

export const OrganizationUsersDialog: FC<OrganizationUsersDialogProps> = ({ open, organization, onClose }) => {
  const { t } = useTranslation()
  const { notify, run } = useMutationFeedback()
  const usersQuery = useGetInternalOrganizationUsersQuery(organization?.id ?? '', { skip: !open || !organization })
  const [setActive] = useSetInternalOrganizationUserActiveMutation()
  const [resetPassword] = useResetInternalUserPasswordMutation()
  const [removeUser] = useRemoveInternalOrganizationUserMutation()
  const [confirm, setConfirm] = useState<ConfirmDialogData<PendingAction>>()
  const [editing, setEditing] = useState<{ open: boolean; user?: InternalUser }>({ open: false })

  const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data])

  const tableColumns: GRTableColumn<InternalUser, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-csm:edit_user_email'),
        accessor: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {row.oadmin && (
              <Tooltip title={t('internal-csm:organization_users_admin')}>
                <Person fontSize="small" color="primary" aria-label={t('internal-csm:organization_users_admin')} />
              </Tooltip>
            )}
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {row.email}
            </Typography>
          </Stack>
        ),
        sortable: true,
        sortAccessor: ({ row }) => row.email.toLowerCase(),
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left', sx: { overflowWrap: 'anywhere' } },
        headerCellProps: { align: 'left', sx: { minWidth: 220 } },
      },
      {
        labelAccessor: t('internal-csm:edit_user_first_name'),
        accessor: ({ row }) => nameOrDash(row.firstName),
        sortable: true,
        sortAccessor: ({ row }) => (row.firstName ?? '').toLowerCase(),
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left', sx: { overflowWrap: 'anywhere' } },
        headerCellProps: { align: 'left' },
      },
      {
        labelAccessor: t('internal-csm:edit_user_last_name'),
        accessor: ({ row }) => nameOrDash(row.lastName),
        sortable: true,
        sortAccessor: ({ row }) => (row.lastName ?? '').toLowerCase(),
        sortOrder: SortOrder.DESC,
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left', sx: { overflowWrap: 'anywhere' } },
        headerCellProps: { align: 'left' },
      },
      {
        labelAccessor: t('internal-csm:organization_users_last_login'),
        accessor: ({ row }) => (row.lastLoginAt > 0 ? format(row.lastLoginAt, 'dd.MM.yyyy, HH:mm') : '---'),
        sortable: true,
        sortAccessor: ({ row }) => row.lastLoginAt,
        headerCellProps: { sx: { minWidth: 140 } },
        cellProps: { sx: { whiteSpace: 'nowrap' } },
      },
      {
        labelAccessor: t('internal-csm:organization_users_status'),
        accessor: ({ row }) => <UserStatusIcon user={row} />,
        sortable: true,
        sortAccessor: ({ row }) => userStatusOrder[getUserStatus(row)],
        headerCellProps: { sx: { minWidth: 72 } },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => (
          <RowActions
            actions={[
              { type: 'edit', label: t('internal-csm:organization_users_edit'), size: 'small', onClick: () => customTableProps?.onEdit(row) },
              {
                icon: row.active ? <PersonOff /> : <Restore />,
                label: t(row.active ? 'internal-csm:organization_users_disable' : 'internal-csm:organization_users_enable'),
                size: 'small',
                onClick: () => customTableProps?.onToggleActive(row),
              },
              {
                icon: <Key />,
                label: t('internal-csm:organization_users_reset_password'),
                size: 'small',
                onClick: () => customTableProps?.onResetPassword(row),
              },
              { type: 'delete', label: t('internal-csm:organization_users_remove'), size: 'small', onClick: () => customTableProps?.onRemove(row) },
            ]}
          />
        ),
        cellProps: { align: 'right', sx: { px: 1 } },
        headerCellProps: { sx: { minWidth: 152 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const organizationName = organization?.name || organization?.id || ''

  const customProps = useMemo<CustomProps>(
    () => ({
      onEdit: (user) => setEditing({ open: true, user }),
      onToggleActive: (user) =>
        setConfirm({
          title: t(user.active ? 'internal-csm:organization_users_disable' : 'internal-csm:organization_users_enable'),
          confirmText: t(user.active ? 'internal-csm:organization_users_disable_confirm' : 'internal-csm:organization_users_enable_confirm', {
            name: getUserDisplayName(user),
          }),
          actionText: t(user.active ? 'internal-csm:organization_users_disable_action' : 'internal-csm:organization_users_enable_action'),
          destructiveAction: user.active,
          data: { kind: 'toggle', user },
        }),
      onResetPassword: (user) =>
        setConfirm({
          title: t('internal-csm:organization_users_reset_password'),
          confirmText: t('internal-csm:organization_users_reset_password_confirm', { name: getUserDisplayName(user), email: user.email }),
          actionText: t('internal-csm:organization_users_reset_password_action'),
          data: { kind: 'reset', user },
        }),
      onRemove: (user) =>
        setConfirm({
          title: t('internal-csm:organization_users_remove'),
          confirmText: t('internal-csm:organization_users_remove_confirm', { name: getUserDisplayName(user), organization: organizationName }),
          actionText: t('internal-csm:organization_users_remove_action'),
          destructiveAction: true,
          data: { kind: 'remove', user },
        }),
    }),
    [organizationName, t]
  )

  const failure = (key: string, error: unknown) => {
    const reason = getValidationErrorMessage(error)
    notify(reason ? `${t(key)}: ${reason}` : t(key), 'error')
  }
  const failureMessages = (key: string) => ({ error: t(key), errorWithReason: (reason: string) => `${t(key)}: ${reason}` })

  const handleConfirm = async (confirmed?: ConfirmDialogData<PendingAction>) => {
    setConfirm(undefined)
    const action = confirmed?.data
    if (!action || !organization) return
    const { user } = action
    if (action.kind === 'toggle') {
      const active = !user.active
      try {
        const { stored } = await setActive({ organizationId: organization.id, username: user.username, active }).unwrap()
        if (stored?.active !== active) notify(t('internal-csm:organization_users_status_not_stored', { email: user.email }), 'error')
        else notify(t('internal-csm:organization_users_status_changed', { email: user.email }), 'success')
      } catch (error) {
        if (isFetchBaseQueryError(error) && error.status === 409) notify(t('internal-csm:organization_users_no_seats'), 'error')
        else failure('internal-csm:organization_users_status_failed', error)
      }
    } else if (action.kind === 'reset') {
      await run(resetPassword({ username: user.username, password: generatePassword(() => utilsService.getRandomPassword()) }).unwrap(), {
        success: t('internal-csm:organization_users_password_reset', { email: user.email }),
        ...failureMessages('internal-csm:organization_users_password_reset_failed'),
      })
    } else {
      const removed = await run(
        removeUser({ organizationId: organization.id, username: user.username }).unwrap(),
        failureMessages('internal-csm:organization_users_remove_failed')
      )
      if (!removed.ok) return
      if (removed.result.stored) notify(t('internal-csm:organization_users_not_removed', { email: user.email }), 'error')
      else notify(t('internal-csm:organization_users_removed', { email: user.email, organization: organizationName }), 'success')
    }
  }

  const exportUsers = (bookType: BookType) => exportOrganizationUsers(bookType, users, organization?.name ?? '')

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <GRDialogTitle onClose={onClose}>
        <Box minWidth={0}>
          {t('internal-csm:organization_users_title', { name: organizationName })}
          {organization && organization.maxSeats > 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('internal-csm:organization_users_seats', { count: organization.userCount, max: organization.maxSeats })}
            </Typography>
          )}
        </Box>
      </GRDialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Person fontSize="small" color="primary" />
            <Typography variant="body2" color="text.secondary">
              {t('internal-csm:organization_users_admin_note')}
            </Typography>
          </Stack>
          <Box flexGrow={1} />
          {users.length > 0 && (
            <ExportDataButton analyticsEventOrigin="Internal Organization Users" onChooseFormat={exportUsers} size="small" variant="outlined" />
          )}
          <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setEditing({ open: true })} disabled={!organization}>
            {t('internal-csm:organization_users_add')}
          </Button>
        </Stack>
        <QueryStateBoundary isLoading={usersQuery.isLoading} isError={usersQuery.isError} errorText={t('internal-csm:organization_users_load_failed')}>
          {users.length ? (
            <GRTable
              rows={users}
              columns={columns}
              onColumnsUpdated={setColumns}
              rowIdKey="id"
              customProps={customProps}
              noRowsLabel={t('internal-common:no_rows')}
              striped
              hoverable
            />
          ) : (
            <Typography>{t('internal-csm:organization_users_none')}</Typography>
          )}
        </QueryStateBoundary>
      </DialogContent>
      {organization && (
        <EditUserDialog
          open={editing.open}
          organization={organization}
          user={editing.user}
          onClose={() => setEditing((current) => ({ ...current, open: false }))}
        />
      )}
      <ConfirmDialog open={!!confirm} confirmDialogData={confirm} onClose={handleConfirm} />
    </Dialog>
  )
}
